import React from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  MAX_MCQ_OPTIONS,
  MIN_MCQ_OPTIONS,
  addMcqOption,
  removeMcqOption,
  updateMcqOptionText,
} from "../utils/mcqQuestion";
import "./MultipleChoiceEditor.css";

const optionLabel = (index) => String.fromCharCode(65 + index);

const MultipleChoiceEditor = ({
  options,
  correctOptionId,
  onOptionsChange,
  onCorrectOptionIdChange,
  errors = [],
  idPrefix = "mcq",
}) => {
  const safeOptions = Array.isArray(options) ? options : [];

  const handleRemove = (optionId) => {
    const next = removeMcqOption(safeOptions, correctOptionId, optionId);
    onOptionsChange(next.options);
    if (next.correctOptionId !== correctOptionId) {
      onCorrectOptionIdChange(next.correctOptionId);
    }
  };

  return (
    <fieldset className="mcq-editor">
      <legend>Answer options</legend>
      <p className="mcq-editor-help">
        Add {MIN_MCQ_OPTIONS}–{MAX_MCQ_OPTIONS} options and select the one correct answer.
      </p>

      {errors.length > 0 && (
        <div className="mcq-editor-errors" role="alert">
          <p>Please check the multiple-choice answer:</p>
          <ul>
            {errors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </div>
      )}

      <div className="mcq-options-list">
        {safeOptions.map((option, index) => {
          const label = optionLabel(index);
          const inputId = `${idPrefix}-option-${option.id || index}`;
          const radioId = `${inputId}-correct`;

          return (
            <div className="mcq-option-row" key={option.id || `${idPrefix}-${index}`}>
              <span className="mcq-option-label" aria-hidden="true">{label}</span>
              <label className="mcq-option-input-label" htmlFor={inputId}>
                <span className="sr-only">Option {label}</span>
                <input
                  id={inputId}
                  className="mcq-option-input"
                  type="text"
                  value={option.text || ""}
                  onChange={(event) =>
                    onOptionsChange(updateMcqOptionText(safeOptions, option.id, event.target.value))
                  }
                  placeholder={`Option ${label}`}
                />
              </label>
              <label className="mcq-correct-choice" htmlFor={radioId}>
                <input
                  id={radioId}
                  type="radio"
                  name={`${idPrefix}-correct-option`}
                  checked={correctOptionId === option.id}
                  onChange={() => onCorrectOptionIdChange(option.id)}
                />
                Correct answer
              </label>
              <button
                type="button"
                className="mcq-remove-option"
                onClick={() => handleRemove(option.id)}
                disabled={safeOptions.length <= MIN_MCQ_OPTIONS}
                aria-label={`Remove option ${label}`}
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mcq-editor-footer">
        <button
          type="button"
          className="mcq-add-option"
          onClick={() => onOptionsChange(addMcqOption(safeOptions))}
          disabled={safeOptions.length >= MAX_MCQ_OPTIONS}
        >
          <Plus size={16} aria-hidden="true" /> Add option
        </button>
        <span>{safeOptions.length} of {MAX_MCQ_OPTIONS} options</span>
      </div>
    </fieldset>
  );
};

export default MultipleChoiceEditor;
